import React, { useEffect, useState } from 'react';
import { translate } from '../../i18n';
import DateSelector from './calendar/dateSelector';
import {
  encodeDateForUrl,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  validateDateRange,
} from '../../utils/dateUtils';
import { Detail } from 'react-calendar/dist/cjs/shared/types';
import bgColor from '../../store/bgColor';
import ReactCalendar from 'react-calendar';
import MyAvailabilityForward from '../../assets/icon/MyAvailabilityForward';
import MyAvailabiltyBackward from '../../assets/icon/MyAvailabilityBackward';
import AvailabilityStatusCard from './AvailabilityEdit';
import calenderApi from '../../api/calenderApi';
import { format } from 'date-fns';
import { AvailabilityForStaff } from '../../type/availablityFOrStaff';
import useUserStore from '../../store/userStore';
import { toast } from 'react-toastify';
import useBgColorStore from '../../store/bgColor';
import { useTheme } from '../../useTheme';
import Loader from '../../assets/loader/Loader';

const AvailabilityCalendar = () => {
  const { theme, toggleTheme } = useTheme();
  const isDarkMode = theme === 'dark';
  const strokeColor = theme === 'dark' ? '#FFFFFF' : '#292A34';

  const [selectedFromDate, setSelectedFromDate] =
    useState<Date>(getFirstDayOfMonth());
  const [selectedToDate, setSelectedToDate] = useState<Date>(
    new Date(getLastDayOfMonth()),
  );
  const [editMode, setEditMode] = useState(false);
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [availabilityData, setAvailabilityData] = useState<
    AvailabilityForStaff[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [calendarStartDate, setCalendarStartDate] =
    useState<Date>(selectedFromDate);
  const staffId = useUserStore((state) => state?.user?.staffId);
  const staffName = useUserStore((state) => state?.user?.staffName);

  const handleFromDateChange = (date: Date | null) => {
    if (date) {
      setSelectedFromDate(date);
      setCalendarStartDate(new Date(date.getFullYear(), date.getMonth(), 1));
      if (validateDateRange(date, selectedToDate)) {
        setSelectedToDate(date);
      }
    }
  };

  const handleToDateChange = (date: Date | null) => {
    if (date) {
      setSelectedToDate(date);
    }
  };

  const handleDayClick = (value: Date) => {
    if (!editMode) {
      return;
    }

    const valueKey = encodeDateForUrl(value);
    const fromKey = encodeDateForUrl(selectedFromDate);
    const toKey = encodeDateForUrl(selectedToDate);
    if (valueKey < fromKey || valueKey > toKey) {
      return;
    }

    const key = encodeDateForUrl(value);
    if (selectedDates?.includes(key)) {
      setSelectedDates(selectedDates?.filter((k) => k !== key));
    } else {
      setSelectedDates([...selectedDates, key]);
    }
  };

  const fetchAvailability = async () => {
    try {
      const formattedFromDate = format(selectedFromDate, 'yyyy-MM-dd');
      const formattedToDate = format(selectedToDate, 'yyyy-MM-dd');
      if (typeof staffId !== 'number') return;
      setLoading(true);
      setSelectedDates([]);
      setEditMode(false);

      const response = await calenderApi.getAvailabiltyforStaff(
        staffId,
        formattedFromDate,
        formattedToDate,
      );
      setAvailabilityData(response?.data);
      // Clear selected dates after successful update
      setSelectedDates([]);
      // Turn off edit mode after successful update
      setEditMode(false);
      setLoading(false);
    } catch (error) {
      toast.error(translate('myAvalabilityCalender.failedToFetch'));
    }
  };

  useEffect(() => {
    if (selectedFromDate && selectedToDate) {
      fetchAvailability();
      setCalendarStartDate(
        new Date(
          selectedFromDate.getFullYear(),
          selectedFromDate.getMonth(),
          1,
        ),
      );
    }
  }, [selectedFromDate, selectedToDate]);
  const { bgColor, updateBgColor } = useBgColorStore();
  return (
    <div className={`flex flex-col w-full gap-4 p-6 ${bgColor}`}>
      <div className="text-start font-medium text-xl text-light-black">
        <h2 className="text-text-primary">
          {translate('myAvalabilityCalender.title', { name: staffName })}
        </h2>
      </div>

      <div className="bg-backgroundColor-white rounded-lg flex justify-between">
        <div className="flex z-20 flex-col md:flex-row p-3">
          <div className="text-start text-base font-medium text-light-black mr-4 ">
            <label className="text-textColorGray">
              {translate('common.from')}
            </label>
            <div className="flex items-center text-sm font-normal text-light-black space-x-1 h-[38px] p-2 rounded border border-customBorder-border ">
              <DateSelector
                onDateChange={handleFromDateChange}
                initialDate={selectedFromDate}
                showArrows={true}
              />
            </div>
          </div>
          <div className="text-start text-base font-medium text-light-black mr-4">
            <label className="text-textColorGray">
              {translate('common.to')}
            </label>
            <div className="flex items-center text-sm font-normal text-light-black h-[38px] p-2 rounded border border-customBorder-border">
              <DateSelector
                onDateChange={handleToDateChange}
                initialDate={selectedToDate}
                showArrows={true}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row w-full gap-4">
        <div className="w-full md:w-3/5 border border-light-blue relative">
          {(loading) && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-20">
              <Loader />
            </div>
          )}
          <ReactCalendar
            key={
              selectedFromDate.toISOString() +
              selectedToDate.toISOString() +
              availabilityData.length +
              selectedDates.length
            }
            tileClassName={({ date }) => {
              const datekey = encodeDateForUrl(date);
              const fromKey = encodeDateForUrl(selectedFromDate);
              const toKey = encodeDateForUrl(selectedToDate);
              const baseCss = 'w-10 h-10 xs:w-12 xs:h-12 sm:w-14 sm:h-14 md:w-14 md:h-14 lg:w-16 lg:h-16 xl:w-20 xl:h-20 2xl:w-28 2xl:h-28 3xl:w-36 3xl:h-36 !text-blue'
              const abbrevToColorClass: Record<string, string> = {
                A: '!bg-availability-green',
                M: '!bg-availability-yellow',
                N: '!bg-availability-gray',
                U: '!bg-availability-red',
                L: '!bg-availability-blue',
                S: '!bg-availability-lightyellow',
              };

              if (datekey < fromKey || datekey > toKey) {
                return `${baseCss} !bg-off-white !p-0 !rounded-md`;
              }

              const key = encodeDateForUrl(date);
              if (editMode && selectedDates.includes(key)) {
                return `${baseCss} !bg-availability-lightgray !p-0 !rounded-md`;
              }

              const availability = availabilityData?.find(
                (entry) => encodeDateForUrl(new Date(entry.workDate)) === key,
              );

              if (
                availability?.abbrev &&
                abbrevToColorClass[availability?.abbrev]
              ) {
                return `${baseCss} !p-0 !rounded-md ${abbrevToColorClass[availability.abbrev]}`;
              }

              return `${baseCss} !p-0 !rounded-md`;
            }}
            onClickDay={editMode ? handleDayClick : undefined}
            className={`${bgColor} w-full ${!editMode ? 'calendar-disabled' : ''}`}
            next2Label={null}
            prev2Label={null}
            nextLabel={
              <>
                <div className="block dark:hidden">
                  <MyAvailabilityForward stroke="#000000" />
                </div>
                <div className="hidden dark:block">
                  <MyAvailabilityForward stroke="#ffffff" />
                </div>
              </>
            }
            prevLabel={
              <div>
                <div className="block dark:hidden">
                  <MyAvailabiltyBackward color="#000000" />
                </div>
                <div className="hidden dark:block">
                  <MyAvailabiltyBackward color="#FFFFFF" />
                </div>
              </div>
            }
            activeStartDate={calendarStartDate}
            onActiveStartDateChange={({
              activeStartDate,
              view,
            }: {
              action: string | undefined;
              activeStartDate: Date | null;
              view: Detail;
            }) => {
              if (activeStartDate && view === 'month') {
                setCalendarStartDate(activeStartDate);
              }
            }}
          />
        </div>

        <div className="w-full md:w-2/5">
          <AvailabilityStatusCard
            selectedDates={selectedDates}
            setSelectedDates={setSelectedDates}
            fetchAvailability={fetchAvailability}
            editMode={editMode}
            setEditMode={setEditMode}
          />
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
